import { cva } from 'class-variance-authority';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/base';
import { I18nKeyOfNamespace } from '@/i18n/i18n-key-of-namespace';
import { UserRole } from '@/model/User.model';
import { toClassNames } from '@/utils';

interface Props {
  role: UserRole;
  className?: string;
}

const roleBadgeText: Record<UserRole, I18nKeyOfNamespace<'schoolApp'>> = {
  [UserRole.SchoolGroupAdmin]: 'role.school_group_admin',
  [UserRole.SchoolAdmin]: 'role.school_admin',
  [UserRole.Instructor]: 'role.instructor',
  [UserRole.Student]: 'role.student',
  [UserRole.Admin]: 'role.admin', // Unused role in SchoolApp
};

const roleBadgeCva = cva('flex items-center justify-center rounded p-1.5 font-semibold', {
  variants: {
    variant: {
      [UserRole.SchoolGroupAdmin]: 'bg-gradient-to-r from-primary-600 to-primary-700 text-white',
      [UserRole.SchoolAdmin]: 'bg-gradient-to-r from-primary-400 to-primary-500 text-white',
      [UserRole.Instructor]: 'bg-gradient-to-r from-gray-500 to-gray-600 text-white',
      [UserRole.Student]: 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-500',
      [UserRole.Admin]: 'text-gray-500', // Unused role in SchoolApp
    } satisfies Record<UserRole, string>,
  },
});

const RoleBadge: FC<Props> = ({ role, className }) => {
  const { t } = useTranslation('schoolApp');

  return (
    <div className={toClassNames(roleBadgeCva({ variant: role }), className)}>
      <Text variant="xs">{t(roleBadgeText[role])}</Text>
    </div>
  );
};

export default RoleBadge;
